import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import { AddMuxDemuxModalComponent } from "./wizard-modal/add-mux-demux-modal.component";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import {
    ColumnConfig,
    ConfirmOptionsModel,
    FilterColumnParameters,
    FilterOperator,
    NmsTableComponent,
    NmsTableValidatorService,
    PaginatedResponse,
    RequestConfig,
    SortConfig,
    TableDataRuleType,
    TextStyleClasses
} from "@nms-angular-toolkit/nms-table";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_TRANSLATE,
    DEVICE_SERVICE,
    NMS_FEATURES
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Observable, Subscription } from "rxjs";
import { MuxActionModel, MuxDemux, MuxDemuxResponse, MuxField, MuxModel } from "./mux-demux.model";
import { RefreshIntervalService } from "@nms-ng2/app/shared/services/refresh-interval/refresh-interval.service";
import { MuxDemuxService } from "./services/mux-demux.service";
import { NmsToastrLinkService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr-link.service";
import { TableFilterService } from "@nms-ng2/app/shared/services/table-filter/table-filter-service";
import { DwdmMuxDemuxValidationService } from "./services/dwdm-mux-demux-validation.service";
import { MatDialog } from "@angular/material/dialog";
import { NmsImageModalComponent } from "@nms-angular-toolkit/nms-image"
import {
    ConfirmModalOptionsService
} from "@nms-ng2/app/shared/services/confirm/confirm-modal-options.service";
import { ConfirmModalOptions } from "@nms-ng2/app/shared/services/confirm/confirm-modal-options.model";

/**
 * Componente de exibição dos Mux/Demux DWDM.
 */
@Component({
    selector: "dwdm-mux-demux",
    templateUrl: "./dwdm-mux-demux.component.html",
    styleUrls: ["./dwdm-mux-demux.component.scss"]
})
export class DwdmMuxDemuxComponent implements AfterViewInit, OnDestroy {
    private readonly baseUrl: string = "/mux/search-all";

    @ViewChild(NmsTableComponent)
    nmsTableComponent: NmsTableComponent;

    muxModels: MuxModel[];
    allMuxDemus: MuxDemux[];
    mapImages = new Map();
    autoUpdateInterval: number;
    muxProvider: () => Observable<Object>;
    nmsTableChangedSubscription: Subscription;
    nmsTableRulesRemovedSubscription: Subscription;
    options: RequestConfig = {
        serverPagination: {
            baseUrl: this.baseUrl
        }
    };
    columns: ColumnConfig[] = [
        {
            field: "image",
            label: this.translate.instant("mux.demux.modal.image"),
            minWidth: "107px",
            width: "107px",
            unsearchable: true,
            unsortable: true,
            optionalColumn: {
                type: "image",
                tooltip: this.translate.instant("mux.demux.modal.image.tooltip"),
                convert: (row) => this.mapImages.get(row["model"]),
                onAction: (row) => this.onActionImage(row)
            },
        },
        {
            field: MuxField.Name,
            label: this.translate.instant("mux.demux.modal.name"),
            minWidth: "400px",
            width: "400px",
            optionalColumn: {
                type: "text",
                onAction: (mux: MuxDemux) => {
                    this.editInline(mux);
                }
            },
            alwaysVisible: true
        },
        {
            field: MuxField.SerialNumber,
            label: this.translate.instant("mux.demux.modal.serial.number"),
            width: "172px",
            minWidth: "172px"
        },
        {
            field: MuxField.Model,
            label: this.translate.instant("mux.demux.modal.model"),
            minWidth: "140px",
            width: "140px"
        },
        {
            field: MuxField.AssociatedDevices,
            label: this.translate.instant("mux.demux.modal.devices.associated"),
            minWidth: "550px",
            width: "550px",
            optionalColumn: {
                type: "text",
                textCustomOptions: {
                    buildCustomText: (row: MuxDemuxResponse) => {
                        return row.associatedDevices || this.translate.instant("mux.demux.modal.devices.empty.associated");
                    },
                    customClass: (row: MuxDemuxResponse): TextStyleClasses => {
                        return !row.associatedDevices ? "customItalicText" : null;
                    }
                }
            }
        },
        {
            field: "actions",
            label: this.translate.instant("table.actions"),
            optionalColumn: {
                type: "actions"
            },
            width: "5%",
            unsortable: true,
            unsearchable: true,
            actions: [
                {
                    id: "edit",
                    iconName: "edit_square",
                    tooltip: this.translate.instant("mux.table.edit"),
                    onAction: (mux: MuxDemux) => {
                        if (this.muxDemuxService.userHasPermission(this.nmsFeatures.dwdmMuxDemuxConfiguration.feature)) {
                            this.editInline(mux);
                        }
                    }
                },
                {
                    id: "delete",
                    iconName: "delete",
                    tooltip: this.translate.instant("mux.table.delete"),
                    onAction: (mux: MuxDemux) => {
                        if (this.muxDemuxService.userHasPermission(this.nmsFeatures.removeDwdmMuxDemux.feature)) {
                            this.removeInLine([mux]);
                        }
                    }
                }
            ]
        }
    ];

    operators: Array<FilterOperator> = [];
    initialSort: SortConfig[] = [];

    muxActions: MuxActionModel[] = [
        {
            id: "add-mux",
            translateKey: this.translate.instant("mux.table.add"),
            action: () => this.openAddMuxDemuxModal()
        },
        {
            id: "edit-mux",
            translateKey: this.translate.instant("mux.table.edit"),
            action: () => {
                this.handleBeforeEdit();
            }
        },
        {
            id: "delete-mux",
            translateKey: this.translate.instant("mux.table.delete"),
            action: () => this.handleBeforeRemove()
        }
    ];

    loggedUser: string;
    columnsSettingsLabels: object = {};

    constructor(private readonly refreshIntervalService: RefreshIntervalService,
        private readonly muxDemuxService: MuxDemuxService, private readonly tableFilterService: TableFilterService,
        private readonly nmsToastrLinkService: NmsToastrLinkService,
        private readonly dwdmMuxDemuxValidationService: DwdmMuxDemuxValidationService,
        private readonly dialog: MatDialog, @Inject(ModalFactory) private readonly modalFactory: ModalFactory,
        @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
        @Inject(ANGULARJS_ROOTSCOPE) private readonly rootScope: any,
        @Inject(DEVICE_SERVICE) private readonly deviceService: any,
        private readonly nmsTableValidatorService: NmsTableValidatorService,
        private readonly confirmModalOptionsService: ConfirmModalOptionsService,
        @Inject(NMS_FEATURES) private readonly nmsFeatures: any) {

        this.loggedUser = rootScope.loggedUser;
        this.refreshIntervalService.getRefreshInterval((interval) => {
            this.autoUpdateInterval = interval;
        });
        this.muxDemuxService.reloadListEmmiter.subscribe(() => this.updateTableDataAndNotify());

        this.operators = [
            {
                value: "AND",
                label: this.translate.instant("nms-table.filter.and"),
                selected: true
            },
            {
                value: "OR",
                label: this.translate.instant("nms-table.filter.or"),
                selected: false
            }
        ];

        this.initialSort = [
            {
                column: MuxField.Name,
                direction: "asc",
                label: this.translate.instant("mux.demux.modal.name"),
            }
        ];

        this.columnsSettingsLabels = {
            iconTooltip: this.translate.instant("nms.table.organize.columns.tooltip"),
            noColumnFound: this.translate.instant("nms.table.organize.columns.search.not.found"),
            showHideColumns: this.translate.instant("nms.table.show.hide.columns.columns"),
            showHideColumnsTooltip: this.translate.instant("nms.table.show.hide.columns.tooltip"),
            searchColumnsPlaceholder: this.translate.instant("nms.table.organize.columns.search"),
            restoreOrdering: this.translate.instant("nms.table.organize.restore.ordering")
        };
    }

    isTableLoaded(): boolean {
        return this.nmsTableComponent !== undefined;
    }

    ngAfterViewInit() {
        this.getMuxModelsImages();
        this.retrieveTableMuxDemux = this.retrieveTableMuxDemux.bind(this);
        this.subscribeTableChange();
        this.subscribeTableRulesRemovedEvent();
    }

    ngOnDestroy(): void {
        this.nmsTableChangedSubscription.unsubscribe();
        this.nmsTableRulesRemovedSubscription.unsubscribe();
    }

    subscribeTableChange() {
        this.nmsTableChangedSubscription = this.nmsTableComponent.contentChangeEvent.subscribe((muxDemux) => {
            Promise.all(muxDemux).then(() => {
                this.nmsTableComponent.applyLoading.next(false);
            });
        });
    }

    processFilterChanges = (filterParameters: FilterColumnParameters) => {
        const requestFilters = this.tableFilterService.processFilterChanges(filterParameters);
        this.nmsTableComponent.filter(requestFilters);
    };

    /**
     * Exibe mensagem de sucesso ao limpar todos os filtros/ordenações.
     */
    private subscribeTableRulesRemovedEvent() {
        this.nmsTableRulesRemovedSubscription = this.nmsTableComponent.tableRulesRemovedEvent
            .subscribe((ruleType: TableDataRuleType) => {
                let message = this.translate.instant("nms.table.view.rule.sort.remove.all");

                if (ruleType === "filter") {
                    message = this.translate.instant("nms.table.view.rule.filtering.remove.all");
                }

                this.nmsToastrLinkService.showToastSuccess(message, []);
            });
    }

    openAddMuxDemuxModal(): void {
        if (this.muxDemuxService.userHasPermission(this.nmsFeatures.dwdmMuxDemuxConfiguration.feature)) {
            this.modalFactory.openAsyncModal(AddMuxDemuxModalComponent);
        }
    }

    retrieveTableMuxDemux(): Observable<Object> {
        return this.nmsTableComponent.getProvider();
    }

    notifyTableUpdateData({ data }: { data: PaginatedResponse }) {
        this.nmsTableComponent.updateData(data);
    }

    onActionImage(row) {
        this.dialog.open(NmsImageModalComponent, {
            data: {
                image: this.mapImages.get(row.model),
                altImage: row.model,
                title: row.model,
            },
            width: "600px",
            height: "480px"
        });
    }

    private async getMuxModelsImages() {
        this.nmsTableComponent.applyLoading.next(true);
        this.muxModels = await this.muxDemuxService.getMuxModels().toPromise();
        for (const model of this.muxModels) {
            const image = await this.deviceService.getDeviceImageByModel(model.name);
            const encodedImage = {
                "background-image": `url('data:image/svg+xml;utf8,${encodeURIComponent(image)}')`
            };
            this.mapImages.set(model.name, encodedImage);
        }
        this.nmsTableComponent.applyLoading.next(false);
    }

    /**
     * Método responsável por atualizar os dados da tabela após uma operação (adição, edição ou remoção) ser com sucesso.
     * O método busca os dados atualizado no backend e envia os novos dados para o nms-table.
     */
    private updateTableDataAndNotify() {
        this.retrieveTableMuxDemux().subscribe((paginatedResponse: PaginatedResponse) => {
            this.notifyTableUpdateData({ data: paginatedResponse });
        });
    }

    /**
     * Realiza checagens antes de abrir a modal para edição.
     */
    private handleBeforeEdit() {
        if (this.muxDemuxService.userHasPermission(this.nmsFeatures.dwdmMuxDemuxConfiguration.feature) &&
            this.dwdmMuxDemuxValidationService.isValidForEdit(this.nmsTableComponent.selection.selected.length, false)) {
            this.openEditModal(this.nmsTableComponent.selection.selected[0]);
        }
    }

    private editInline(mux: MuxDemux) {
        this.openEditModal(mux);
    }

    private openEditModal(mux: MuxDemux) {
        this.muxDemuxService.getMux(mux.resourceId).then(result => {
            this.modalFactory.openAsyncModal(AddMuxDemuxModalComponent, { muxDemuxInfo: result });
        });
    }

    private handleBeforeRemove() {
        if (this.muxDemuxService.userHasPermission(this.nmsFeatures.removeDwdmMuxDemux.feature) &&
            this.dwdmMuxDemuxValidationService.isValidForRemove(this.nmsTableComponent.selection.selected.length)) {
            const muxSelected: MuxDemux[] = this.nmsTableComponent.selection.selected;
            this.removeInLine(muxSelected);
        }
    }

    private removeInLine(mux: MuxDemux[]) {
        this.nmsTableValidatorService.showConfirmOptions(this.buildMessageForConfirmOptions())
            .then(() => {
                this.muxDemuxService.removeMuxDemux(mux);
            });
    }

    private buildMessageForConfirmOptions(): ConfirmOptionsModel {
        const confirmModalOptions = {
            rowsSelectedLength: this.nmsTableComponent.selection.selected.length,
            singleSelectedRowKey: "mux.table.confirm.remove.singleDescription",
            multipleSelectedRowsKey: "mux.table.confirm.remove.pluralDescription",
            title: "mux.table.confirm"
        } as ConfirmModalOptions;

        return this.confirmModalOptionsService.getMessageForConfirmOptions(confirmModalOptions);
    }

}
